"use client"

import { useState } from "react"
import { ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"

interface FAQItem {
  question: string
  answer: string
}

// Semantic <dl>/<dt>/<dd> accordion. Every answer stays mounted in the DOM —
// collapsed answers are hidden with CSS (`hidden`), never conditionally
// rendered — so crawlers and the FAQPage/Speakable schema selectors
// (.geo-faq dt / .geo-faq dd in app/faq/page.tsx) always resolve against the
// full question + answer text. Wrapping each dt/dd pair in a <div> inside
// <dl> is valid HTML and keeps the per-item card border.
export default function FAQContent({ items }: { items: FAQItem[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <dl className="space-y-3">
      {items.map((item, idx) => {
        const isOpen = openIndex === idx
        return (
          <div
            key={idx}
            className={cn(
              "rounded-xl border transition-colors",
              isOpen
                ? "border-purple-300 dark:border-purple-800/50 bg-white dark:bg-zinc-900"
                : "border-gray-200 dark:border-zinc-800 bg-white/50 dark:bg-zinc-900/50"
            )}
          >
            <dt>
              <button
                type="button"
                onClick={() => setOpenIndex(isOpen ? null : idx)}
                aria-expanded={isOpen}
                aria-controls={`faq-answer-${idx}`}
                className="w-full flex items-center justify-between p-5 text-left gap-4"
              >
                <span className="font-medium text-sm lg:text-base text-gray-900 dark:text-white">
                  {item.question}
                </span>
                <ChevronDown
                  className={cn(
                    "w-5 h-5 shrink-0 text-gray-400 dark:text-zinc-500 transition-transform duration-200",
                    isOpen && "rotate-180"
                  )}
                />
              </button>
            </dt>
            <dd id={`faq-answer-${idx}`} className={cn("px-5 pb-5 -mt-1", !isOpen && "hidden")}>
              <p className="text-sm lg:text-base text-gray-600 dark:text-neutral-400 leading-relaxed">
                {item.answer}
              </p>
            </dd>
          </div>
        )
      })}
    </dl>
  )
}
